import { useAtom } from "jotai";
import {
  CompanySettingDataAtom,
  CompanySettingType,
  employeeInfoAtom,
  EmployeeInfoType,
  splashImageInfoAtom,
  loginImageInfoAtom,
  LoginImageInfoType,
  LanguageLabelsType,
  LanguageLabelsAtom,
  SplashImageInfoType,
  splashImageInfoInitial,
  recordingSettingAtom,
} from "../store";
import { useCallback, useRef } from "react";
import { voislogApi } from "./apiClients/voislogApi";
import { useToastMessage } from "../hooks/useToastMessage";
import { sentryLog } from "../../sentry";
import { AccessToken } from "../types";

type SettingInfo = (token: AccessToken) => void;

/*
 * vManagement API からアプリ設定を取得して各 atom に保持する共通処理
 */
export function useSetSetting(): SettingInfo {
  const lastEmployeeId = useRef<string | null>(null);
  const [, setEmployeeInfo] = useAtom(employeeInfoAtom);
  const [, setSplashImageInfo] = useAtom(splashImageInfoAtom);
  const [, setLoginImageInfo] = useAtom(loginImageInfoAtom);
  const [, setLanguageLabels] = useAtom(LanguageLabelsAtom);
  const [, setCompanySettingData] = useAtom(CompanySettingDataAtom);
  const [, setRecordingSetting] = useAtom(recordingSettingAtom);

  const errorToast = useToastMessage();

  return useCallback(
    async (token) => {
      // 同じユーザーの場合は何もしない
      if (lastEmployeeId.current === token.user_id) {
        return;
      }

      // Splash画像パスの初期化
      setSplashImageInfo(splashImageInfoInitial);

      // vManagement API から各設定を並列取得
      try {
        const [bannerRes, splashRes, recTypeRes, appLabelRes, recordingRes] =
          await Promise.all([
            voislogApi.GET("/v2/v_management/app_settings/image/{image_type}", {
              params: {
                path: { image_type: "banner" },
                query: { user_id: token.user_id },
              },
            }),
            voislogApi.GET("/v2/v_management/app_settings/image/{image_type}", {
              params: {
                path: { image_type: "splash" },
                query: { user_id: token.user_id },
              },
            }),
            voislogApi.GET("/v2/v_management/app_settings/rec_type", {
              params: {
                query: { company_id: token.company_id },
              },
            }),
            voislogApi.GET("/v2/v_management/settings/app_label", {
              params: {
                query: { company_id: token.company_id },
              },
            }),
            voislogApi.GET("/v2/v_management/app_settings/recording", {
              params: {
                query: { company_id: token.company_id },
              },
            }),
          ]);

        const bannerData = bannerRes.data!;
        const splashData = splashRes.data!;
        const recTypeData = recTypeRes.data!;
        const appLabelData = appLabelRes.data!;
        const recordingData = recordingRes.data!;

        // ユーザー情報を保存
        // employee情報取得（Core API /users/me）
        const meRes = await voislogApi.GET("/v2/core/users/{user_id_or_me}", {
          params: { path: { user_id_or_me: token.user_id } },
        });
        const me = meRes.data!;
        const employeeInfo: EmployeeInfoType = {
          employee_id: me.email,
          employee_name: `${me.last_name} ${me.first_name}`,
          company_id: me.companies?.find((c) => c.is_member)?.id ?? "",
        };
        setEmployeeInfo(employeeInfo);
        lastEmployeeId.current = token.user_id;

        // banner: ロゴ画像（ログイン画面用）
        const loginImageInfo: LoginImageInfoType = {
          login_path: bannerData.setting_data?.image_url ?? "",
          login_no: bannerData.setting_data?.no ?? null,
        };
        setLoginImageInfo(JSON.stringify(loginImageInfo));

        // splash: スプラッシュ画像
        const splashImageInfo: SplashImageInfoType = {
          splash_path: splashData.setting_data?.image_url ?? "",
          splash_no: splashData.setting_data?.no ?? null,
        };
        setSplashImageInfo(splashImageInfo);

        // rec_type: 録音種別設定
        const rec_types = recTypeData.setting_data?.rec_types ?? undefined;
        const companySetting: CompanySettingType = { rec_types };
        setCompanySettingData(companySetting);

        // app_label: 表記設定
        setLanguageLabels(appLabelData.setting_data as LanguageLabelsType);

        // recording: 録音設定
        setRecordingSetting(recordingData);
      } catch (exc) {
        sentryLog(exc);
        errorToast(exc);
      }
    },
    [
      errorToast,
      setEmployeeInfo,
      setSplashImageInfo,
      setLoginImageInfo,
      setLanguageLabels,
      setCompanySettingData,
      setRecordingSetting,
    ],
  );
}
